import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Printer, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Contact person mapping for SPO print
const PURCHASE_CONTACTS = {
  'Manish Gupta': '+91 8263845649',
  'Umesh Bhosale': '+91 9822242371',
  'Gopal Vyas': '+91 7498392817'
};

// Get contact info based on creator name
const getContactInfo = (createdByName) => {
  if (!createdByName) return { name: 'Purchase Team', phone: '' };
  
  for (const [name, phone] of Object.entries(PURCHASE_CONTACTS)) {
    if (createdByName.toLowerCase().includes(name.toLowerCase().split(' ')[0])) {
      return { name, phone };
    }
  }
  
  const firstContact = Object.entries(PURCHASE_CONTACTS)[0];
  return { name: firstContact[0], phone: firstContact[1] };
};

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

const SPOPrint = ({ spo, autoPrint = false, onPrintComplete }) => {
  const hasTriggeredPrint = useRef(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (autoPrint && spo && !hasTriggeredPrint.current) {
      hasTriggeredPrint.current = true;
      setTimeout(() => {
        handlePrintClick();
      }, 500);
    }
  }, [autoPrint, spo]);

  // Fetch full SPO data with supplier_details before printing
  const fetchFullSPO = async (spoId) => {
    try {
      const response = await axios.get(`${API_URL}/service-purchase-orders/${spoId}`, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch SPO details:', error);
      return null;
    }
  };

  const handlePrintClick = async () => {
    if (!spo) return;
    
    setIsLoading(true);
    try {
      // Fetch full SPO data with supplier_details
      const fullSpo = await fetchFullSPO(spo.id);
      const spoToPrint = fullSpo || spo;
      handlePrint(spoToPrint);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = (spoData) => {
    if (!spoData) return;

    const serviceItems = spoData.service_items || [];
    const contactInfo = getContactInfo(spoData.submitted_by_name || spoData.created_by_name);
    
    // Build service items table rows
    const serviceItemRows = serviceItems.map((item, idx) => `
      <tr>
        <td style="text-align: center; padding: 8px; border: 1px solid #ddd;">${idx + 1}</td>
        <td style="padding: 8px; border: 1px solid #ddd;">
          <div style="font-weight: 500;">${item.description}</div>
        </td>
        <td style="text-align: right; padding: 8px; border: 1px solid #ddd;">${item.quantity}</td>
        <td style="text-align: center; padding: 8px; border: 1px solid #ddd;">${item.unit || 'NOS'}</td>
        <td style="text-align: right; padding: 8px; border: 1px solid #ddd;">₹${(item.rate || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
        <td style="text-align: right; padding: 8px; border: 1px solid #ddd;">₹${(item.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
      </tr>
    `).join('');

    // Calculate totals
    const subTotal = spoData.sub_total || serviceItems.reduce((sum, item) => sum + (item.amount || 0), 0);
    const gstRate = spoData.gst_rate || 0;
    const gstAmount = spoData.gst_amount || 0;
    const otherCharges = spoData.other_charges || 0;
    const grandTotal = spoData.grand_total || (subTotal + gstAmount + otherCharges);

    // Format dates
    const formatDate = (dateStr) => {
      if (!dateStr) return '-';
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
      } catch {
        return dateStr;
      }
    };

    // Company logo URL
    const logoUrl = 'https://customer-assets.emergentagent.com/job_erp-bugfix-8/artifacts/isk2zb36_Logo%20final.png';

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Service Purchase Order - ${spoData.spo_number}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            font-size: 12px; 
            padding: 20px;
            max-width: 210mm;
            margin: 0 auto;
          }
          .header { 
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            border-bottom: 2px solid #333; 
            padding-bottom: 15px; 
            margin-bottom: 20px; 
          }
          .header-left {
            flex: 0 0 150px;
          }
          .header-left img {
            max-width: 140px;
            height: auto;
          }
          .header-right {
            flex: 1;
            text-align: right;
          }
          .header-right h1 { 
            font-size: 16px; 
            margin-bottom: 5px;
            color: #333;
          }
          .header-right p {
            margin: 2px 0;
            color: #555;
            font-size: 11px;
          }
          .header-right .contact-info {
            margin-top: 8px;
            font-size: 11px;
          }
          .spo-title {
            text-align: center;
            margin-bottom: 15px;
          }
          .spo-title h2 {
            font-size: 20px;
            color: #333;
            margin-bottom: 3px;
            background: #e8f4e8;
            padding: 8px;
            border-radius: 4px;
          }
          .spo-title h3 {
            font-size: 14px;
            color: #666;
          }
          .spo-info { 
            display: flex; 
            justify-content: space-between; 
            margin-bottom: 20px;
            gap: 20px;
          }
          .spo-info-section { 
            flex: 1;
            padding: 12px;
            border: 1px solid #ddd;
            border-radius: 4px;
            background: #fafafa;
          }
          .spo-info-section h3 {
            font-size: 12px;
            color: #333;
            margin-bottom: 8px;
            padding-bottom: 4px;
            border-bottom: 1px solid #ddd;
          }
          .spo-info-section p { 
            margin: 3px 0; 
            color: #555;
            font-size: 11px;
          }
          .spo-info-section strong {
            color: #333;
          }
          .service-description {
            background: #f5f5f5;
            padding: 12px;
            border-radius: 4px;
            margin-bottom: 15px;
            border-left: 4px solid #4CAF50;
          }
          .service-description h4 {
            font-size: 12px;
            color: #333;
            margin-bottom: 5px;
          }
          .service-description p {
            font-size: 13px;
            color: #444;
            font-weight: 500;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 20px;
          }
          th { 
            background: #4CAF50; 
            color: white;
            padding: 10px 6px; 
            border: 1px solid #3d8b40; 
            text-align: left;
            font-weight: bold;
            font-size: 11px;
          }
          td { 
            padding: 8px 6px; 
            border: 1px solid #ddd;
            vertical-align: top;
            font-size: 11px;
          }
          tr:nth-child(even) {
            background: #f9f9f9;
          }
          .totals-section {
            display: flex;
            justify-content: flex-end;
            margin-top: 20px;
          }
          .totals-table {
            width: 320px;
          }
          .totals-table td {
            padding: 8px 12px;
            font-size: 11px;
          }
          .totals-table .label {
            text-align: right;
            color: #555;
          }
          .totals-table .value {
            text-align: right;
            font-weight: bold;
            color: #333;
          }
          .totals-table .grand-total {
            background: #4CAF50;
            color: white;
            font-size: 13px;
          }
          .totals-table .grand-total .label,
          .totals-table .grand-total .value {
            color: white;
          }
          .advance-info {
            background: #fff3cd;
            border: 1px solid #ffc107;
            padding: 12px;
            border-radius: 4px;
            margin-top: 15px;
          }
          .advance-info h4 {
            color: #856404;
            margin-bottom: 8px;
            font-size: 12px;
          }
          .advance-info p {
            color: #856404;
            font-size: 11px;
            margin: 3px 0;
          }
          .footer {
            margin-top: 40px;
            display: flex;
            justify-content: space-between;
          }
          .signature-box {
            width: 180px;
            text-align: center;
          }
          .signature-line {
            border-top: 1px solid #333;
            margin-top: 50px;
            padding-top: 5px;
            font-size: 11px;
          }
          .signature-name {
            font-size: 10px;
            color: #555;
            margin-top: 3px;
          }
          .terms {
            margin-top: 25px;
            padding: 12px;
            background: #fafafa;
            border: 1px solid #ddd;
            border-radius: 4px;
          }
          .terms h4 {
            margin-bottom: 8px;
            color: #333;
            font-size: 12px;
          }
          .terms ol {
            margin-left: 18px;
            color: #555;
            font-size: 11px;
          }
          .terms li {
            margin: 4px 0;
          }
          @media print {
            body { padding: 10px; }
            .spo-title h2 { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            th { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .totals-table .grand-total { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .advance-info { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="header-left">
            <img src="${logoUrl}" alt="Company Logo" />
          </div>
          <div class="header-right">
            <h1>DREAMSPAN VENTURES PRIVATE LIMITED</h1>
            <p>556/1/2, Balaji Hotel Lane, Pisoli</p>
            <p>Katraj Kondhwa Road, Pune-411060</p>
            <p>GSTIN: 27AAHCD8512E1ZR</p>
            <div class="contact-info">
              <strong>Contact:</strong> ${contactInfo.name} ${contactInfo.phone ? `(${contactInfo.phone})` : ''}
            </div>
          </div>
        </div>

        <div class="spo-title">
          <h2>SERVICE PURCHASE ORDER</h2>
          <h3>${spoData.spo_number}</h3>
        </div>

        <div class="spo-info">
          <div class="spo-info-section">
            <h3>Service Provider Details</h3>
            <p><strong>${spoData.supplier_name}</strong></p>
            ${spoData.supplier_details?.address ? `<p>${spoData.supplier_details.address}</p>` : ''}
            ${spoData.supplier_details?.city || spoData.supplier_details?.state ? `<p>${[spoData.supplier_details.city, spoData.supplier_details.state].filter(Boolean).join(', ')}</p>` : ''}
            ${spoData.supplier_details?.gst_number ? `<p><strong>GSTIN:</strong> ${spoData.supplier_details.gst_number}</p>` : ''}
            ${spoData.supplier_details?.contact_person ? `<p><strong>Contact:</strong> ${spoData.supplier_details.contact_person}${spoData.supplier_details?.phone ? ` (${spoData.supplier_details.phone})` : ''}</p>` : ''}
          </div>
          <div class="spo-info-section">
            <h3>Order Details</h3>
            <p><strong>SPO No:</strong> ${spoData.spo_number}</p>
            <p><strong>Date:</strong> ${formatDate(spoData.created_at)}</p>
            <p><strong>Status:</strong> ${spoData.status?.toUpperCase()}</p>
          </div>
        </div>

        <div class="service-description">
          <h4>Service Description</h4>
          <p>${spoData.service_description}</p>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 5%; text-align: center;">S.No</th>
              <th style="width: 45%;">Service Item Description</th>
              <th style="width: 10%; text-align: right;">Qty</th>
              <th style="width: 10%; text-align: center;">Unit</th>
              <th style="width: 15%; text-align: right;">Rate</th>
              <th style="width: 15%; text-align: right;">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${serviceItemRows}
          </tbody>
        </table>

        <div class="totals-section">
          <table class="totals-table">
            <tr>
              <td class="label">Sub Total:</td>
              <td class="value">₹${subTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            </tr>
            ${gstRate > 0 ? `
            <tr>
              <td class="label">GST @ ${gstRate}%:</td>
              <td class="value">₹${gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            </tr>
            ` : ''}
            ${otherCharges > 0 ? `
            <tr>
              <td class="label">Other Charges:</td>
              <td class="value">₹${otherCharges.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            </tr>
            ` : ''}
            <tr class="grand-total">
              <td class="label">Grand Total:</td>
              <td class="value">₹${grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            </tr>
          </table>
        </div>

        ${spoData.advance_requested && spoData.advance_amount > 0 ? `
        <div class="advance-info">
          <h4>⚠️ ADVANCE PAYMENT DETAILS</h4>
          <p><strong>Advance Amount:</strong> ₹${spoData.advance_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
          <p><strong>Payment Due Date:</strong> ${formatDate(spoData.advance_payment_due_date)}</p>
        </div>
        ` : ''}

        ${spoData.notes ? `
        <div class="terms">
          <h4>Notes / Remarks</h4>
          <p>${spoData.notes}</p>
        </div>
        ` : ''}

        <div class="terms">
          <h4>Terms & Conditions</h4>
          <ol>
            <li>This order is subject to our standard terms and conditions.</li>
            <li>All services to be rendered as per agreed specifications and timeline.</li>
            <li>Any changes to scope must be approved in writing.</li>
            <li>Payment will be processed as per our standard payment terms.</li>
            <li>Please quote SPO number in all correspondence and invoices.</li>
          </ol>
        </div>

        <div class="footer">
          <div class="signature-box">
            <div class="signature-line">Prepared By</div>
            <div class="signature-name">${spoData.created_by_name || contactInfo.name}</div>
          </div>
          <div class="signature-box">
            <div class="signature-line">Approved By</div>
            <div class="signature-name">${spoData.approved_by_name || ''}</div>
          </div>
          <div class="signature-box">
            <div class="signature-line">Service Provider</div>
            <div class="signature-name">(Signature & Stamp)</div>
          </div>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          if (onPrintComplete) {
            onPrintComplete();
          }
        }, 250);
      };
    }
  };

  if (!spo) return null;

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handlePrintClick}
      disabled={isLoading}
      title="Print SPO"
    >
      {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
    </Button>
  );
};

export default SPOPrint;
